// Translations for English and Italian
const translations = {
  en: {
    // Navigation
    nav: {
      about: "About",
      services: "Services",
      markets: "Markets",
      howWeWork: "How We Work",
      contact: "Contact",
      businessInquiries: "Business Inquiries",
    },
    // Hero
    hero: {
      tagline: "Pool supply & technical support",
      mainClaim: "We operate across Italy, Canada and the United States through an international supply network.",
      connectLinkedIn: "Connect on LinkedIn",
    },
    // Company Snapshot
    snapshot: {
      title: "Company Snapshot",
      markets: "Markets",
      marketsValue: "Italy • Canada • USA",
      focus: "Focus",
      focusValue: "Pool treatment supplies & operational support",
      clients: "Clients",
      clientsValue: "Hospitality & commercial pools",
      model: "Model",
      modelValue: "Sourcing + direct shipping (based on availability)",
    },
    // About
    about: {
      label: "About Us",
      title: "Built for hospitality operations",
      p1: "We help hospitality and commercial pool operators source the right products and components quickly, with clear guidance and dependable execution. Our international network spans Italy, Canada, and the United States, enabling efficient procurement and delivery across markets.",
      p2: "From chemicals and spare parts to operational support, we focus on what matters: keeping your pools running smoothly with minimal friction and maximum reliability.",
      statsLabel: "International Markets",
      imageAlt: "Luxury infinity pool overlooking the sea with rocky cliffs",
    },
    // Services
    services: {
      label: "What We Do",
      title: "Our Services",
      subtitle: "Comprehensive pool supply and support services designed for hospitality and commercial operations.",
      chemicals: {
        title: "Chemicals & Consumables",
        description: "Essential pool treatment chemicals and consumables from trusted suppliers, available for delivery across our operating markets.",
      },
      spareParts: {
        title: "Spare Parts & Components",
        description: "Source hard-to-find replacement parts and components for pool systems, pumps, and filtration equipment.",
      },
      equipment: {
        title: "Equipment Accessories",
        description: "Valves, fittings, dosing essentials, and other accessories to keep your pool equipment running efficiently.",
      },
      guidance: {
        title: "Operational Guidance",
        description: "Practical support for setup, maintenance routines, and troubleshooting to optimize your pool operations.",
      },
    },
    // Markets
    markets: {
      label: "Global Presence",
      title: "Where We Operate",
      italy: "Italy",
      canada: "Canada",
      usa: "United States",
      note: "Availability and delivery times depend on supplier stock and destination.",
    },
    // Process
    process: {
      label: "Our Process",
      title: "How We Work",
      subtitle: "A straightforward process designed for efficiency and reliability.",
      step1: {
        title: "Request & Requirement Check",
        description: "Share your needs and we'll assess the best options for your operation.",
      },
      step2: {
        title: "Fast Sourcing Options",
        description: "We tap into our supplier network to find available products quickly.",
      },
      step3: {
        title: "Delivery Coordination",
        description: "We coordinate logistics to get supplies to your location efficiently.",
      },
      step4: {
        title: "Follow-up Support",
        description: "Ongoing guidance and support to ensure everything runs smoothly.",
      },
    },
    // Trust
    trust: {
      label: "Why Choose Us",
      title: "Why Claritech",
      reasons: [
        "Clear, fast communication",
        "Reliable sourcing from trusted suppliers",
        "Practical guidance for operations",
        "Built for hospitality operations",
        "International mindset, operational execution",
      ],
      imageAlt: "Modern courtyard pool in elegant hospitality setting with tropical plants",
    },
    // Contact
    contact: {
      label: "Get in Touch",
      title: "Contact Us",
      subtitle: "Ready to discuss your pool supply needs? Get in touch with our team for professional assistance.",
      email: "Email",
      marketsLabel: "Markets",
      note: "Business inquiries only.",
      form: {
        fullName: "Full Name",
        namePlaceholder: "Your name",
        company: "Company",
        companyPlaceholder: "Your company name",
        emailLabel: "Email",
        emailPlaceholder: "your@email.com",
        country: "Country",
        countryPlaceholder: "Select your country",
        countryOther: "Other",
        message: "Message",
        messagePlaceholder: "How can we help you?",
        submit: "Send Message",
        sending: "Sending...",
        success: "Thank you! Your message has been sent successfully.",
        error: "Something went wrong. Please try again or email us directly.",
      },
    },
    // Footer
    footer: {
      privacy: "Privacy Policy",
      terms: "Terms",
      copyright: "All rights reserved.",
    },
    // Privacy Page
    privacy: {
      title: "Privacy Policy",
      lastUpdated: "Last updated: January 2026",
      sections: [
        {
          title: "1. Information We Collect",
          content: "When you use our website or contact us, we may collect the following information:",
          list: [
            "Name and contact information (email address, company name)",
            "Information you provide through our contact form",
            "Technical data such as IP address, browser type, and device information",
          ],
        },
        {
          title: "2. How We Use Your Information",
          content: "We use the information we collect to:",
          list: [
            "Respond to your inquiries and provide customer support",
            "Process and fulfill business requests",
            "Improve our website and services",
            "Comply with legal obligations",
          ],
        },
        {
          title: "3. Data Protection",
          content: "We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.",
        },
        {
          title: "4. Your Rights",
          content: "Depending on your location, you may have the right to:",
          list: [
            "Access your personal data",
            "Correct inaccurate data",
            "Request deletion of your data",
            "Object to processing of your data",
          ],
        },
        {
          title: "5. Contact Us",
          content: "If you have any questions about this Privacy Policy, please contact us at",
        },
      ],
    },
    // Terms Page
    terms: {
      title: "Terms of Service",
      lastUpdated: "Last updated: January 2026",
      sections: [
        {
          title: "1. Acceptance of Terms",
          content: "By accessing and using the Claritech Pool Systems website, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our website.",
        },
        {
          title: "2. Services",
          content: "Claritech Pool Systems provides pool supply sourcing, technical support, and related services for hospitality and commercial pool operators. All services are subject to availability and may vary by market and supplier stock.",
        },
        {
          title: "3. Business Inquiries",
          content: "This website is intended for business-to-business communications only. By submitting an inquiry, you represent that you are acting on behalf of a legitimate business entity.",
        },
        {
          title: "4. Intellectual Property",
          content: "All content on this website, including text, graphics, logos, and images, is the property of Claritech Pool Systems and is protected by applicable intellectual property laws.",
        },
        {
          title: "5. Limitation of Liability",
          content: "Claritech Pool Systems shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use our services or website.",
        },
        {
          title: "6. Governing Law",
          content: "These Terms shall be governed by and construed in accordance with applicable laws, without regard to conflict of law principles.",
        },
        {
          title: "7. Contact",
          content: "For questions about these Terms, please contact us at",
        },
      ],
    },
  },
  it: {
    // Navigation
    nav: {
      about: "Chi siamo",
      services: "Servizi",
      markets: "Mercati",
      howWeWork: "Come lavoriamo",
      contact: "Contatti",
      businessInquiries: "Richieste commerciali",
    },
    // Hero
    hero: {
      tagline: "Fornitura prodotti piscina e supporto tecnico",
      mainClaim: "Operiamo tra Italia, Canada e Stati Uniti attraverso una rete internazionale di fornitura.",
      connectLinkedIn: "Collegati su LinkedIn",
    },
    // Company Snapshot
    snapshot: {
      title: "Profilo aziendale",
      markets: "Mercati",
      marketsValue: "Italia • Canada • USA",
      focus: "Focus",
      focusValue: "Forniture per trattamento piscine e supporto operativo",
      clients: "Clienti",
      clientsValue: "Hospitality e piscine commerciali",
      model: "Modello",
      modelValue: "Sourcing + spedizione diretta (in base alla disponibilità)",
    },
    // About
    about: {
      label: "Chi siamo",
      title: "Costruito per le operazioni di hospitality",
      p1: "Aiutiamo operatori di piscine commerciali e del settore hospitality a trovare rapidamente i prodotti e i componenti giusti, con una guida chiara e un'esecuzione affidabile. La nostra rete internazionale copre Italia, Canada e Stati Uniti, permettendo un approvvigionamento efficiente e consegne in tutti i mercati.",
      p2: "Dai prodotti chimici ai ricambi, fino al supporto operativo, ci concentriamo su ciò che conta: mantenere le vostre piscine in funzione senza intoppi, con minime frizioni e massima affidabilità.",
      statsLabel: "Mercati internazionali",
      imageAlt: "Piscina infinity di lusso con vista sul mare e scogliere rocciose",
    },
    // Services
    services: {
      label: "Cosa facciamo",
      title: "I nostri servizi",
      subtitle: "Servizi completi di fornitura e supporto per piscine, progettati per operazioni hospitality e commerciali.",
      chemicals: {
        title: "Prodotti chimici e consumabili",
        description: "Prodotti chimici essenziali e consumabili per il trattamento piscine da fornitori affidabili, disponibili per la consegna nei nostri mercati operativi.",
      },
      spareParts: {
        title: "Ricambi e componenti",
        description: "Ricerca di ricambi e componenti difficili da trovare per sistemi piscina, pompe e apparecchiature di filtrazione.",
      },
      equipment: {
        title: "Accessori per attrezzature",
        description: "Valvole, raccordi, essenziali per il dosaggio e altri accessori per mantenere le attrezzature della piscina in funzione in modo efficiente.",
      },
      guidance: {
        title: "Supporto operativo",
        description: "Supporto pratico per installazione, routine di manutenzione e risoluzione problemi per ottimizzare le operazioni della piscina.",
      },
    },
    // Markets
    markets: {
      label: "Presenza globale",
      title: "Dove operiamo",
      italy: "Italia",
      canada: "Canada",
      usa: "Stati Uniti",
      note: "Disponibilità e tempi di consegna dipendono dallo stock del fornitore e dalla destinazione.",
    },
    // Process
    process: {
      label: "Il nostro processo",
      title: "Come lavoriamo",
      subtitle: "Un processo semplice progettato per efficienza e affidabilità.",
      step1: {
        title: "Richiesta e verifica requisiti",
        description: "Condividi le tue esigenze e valuteremo le migliori opzioni per la tua operazione.",
      },
      step2: {
        title: "Opzioni di sourcing rapido",
        description: "Attingiamo alla nostra rete di fornitori per trovare rapidamente i prodotti disponibili.",
      },
      step3: {
        title: "Coordinamento consegne",
        description: "Coordiniamo la logistica per far arrivare le forniture nella tua sede in modo efficiente.",
      },
      step4: {
        title: "Supporto post-vendita",
        description: "Guida e supporto continui per garantire che tutto funzioni senza problemi.",
      },
    },
    // Trust
    trust: {
      label: "Perché sceglierci",
      title: "Perché Claritech",
      reasons: [
        "Comunicazione chiara e veloce",
        "Sourcing affidabile da fornitori fidati",
        "Guida pratica per le operazioni",
        "Costruito per operazioni hospitality",
        "Mentalità internazionale, esecuzione operativa",
      ],
      imageAlt: "Piscina moderna in cortile in elegante contesto hospitality con piante tropicali",
    },
    // Contact
    contact: {
      label: "Contattaci",
      title: "Contattaci",
      subtitle: "Pronto a discutere le tue esigenze di fornitura per piscine? Contatta il nostro team per assistenza professionale.",
      email: "Email",
      marketsLabel: "Mercati",
      note: "Solo richieste commerciali.",
      form: {
        fullName: "Nome completo",
        namePlaceholder: "Il tuo nome",
        company: "Azienda",
        companyPlaceholder: "Nome della tua azienda",
        emailLabel: "Email",
        emailPlaceholder: "tua@email.com",
        country: "Paese",
        countryPlaceholder: "Seleziona il tuo paese",
        countryOther: "Altro",
        message: "Messaggio",
        messagePlaceholder: "Come possiamo aiutarti?",
        submit: "Invia messaggio",
        sending: "Invio in corso...",
        success: "Grazie! Il tuo messaggio è stato inviato con successo.",
        error: "Qualcosa è andato storto. Riprova o scrivici direttamente via email.",
      },
    },
    // Footer
    footer: {
      privacy: "Privacy Policy",
      terms: "Termini",
      copyright: "Tutti i diritti riservati.",
    },
    // Privacy Page
    privacy: {
      title: "Privacy Policy",
      lastUpdated: "Ultimo aggiornamento: Gennaio 2026",
      sections: [
        {
          title: "1. Informazioni che raccogliamo",
          content: "Quando utilizzi il nostro sito web o ci contatti, potremmo raccogliere le seguenti informazioni:",
          list: [
            "Nome e informazioni di contatto (indirizzo email, nome dell'azienda)",
            "Informazioni fornite tramite il nostro modulo di contatto",
            "Dati tecnici come indirizzo IP, tipo di browser e informazioni sul dispositivo",
          ],
        },
        {
          title: "2. Come utilizziamo le tue informazioni",
          content: "Utilizziamo le informazioni raccolte per:",
          list: [
            "Rispondere alle tue richieste e fornire supporto clienti",
            "Elaborare e soddisfare richieste commerciali",
            "Migliorare il nostro sito web e i nostri servizi",
            "Rispettare gli obblighi legali",
          ],
        },
        {
          title: "3. Protezione dei dati",
          content: "Implementiamo misure tecniche e organizzative appropriate per proteggere le tue informazioni personali da accessi non autorizzati, alterazioni, divulgazione o distruzione.",
        },
        {
          title: "4. I tuoi diritti",
          content: "A seconda della tua posizione, potresti avere il diritto di:",
          list: [
            "Accedere ai tuoi dati personali",
            "Correggere dati inesatti",
            "Richiedere la cancellazione dei tuoi dati",
            "Opporti al trattamento dei tuoi dati",
          ],
        },
        {
          title: "5. Contattaci",
          content: "Se hai domande su questa Privacy Policy, contattaci a",
        },
      ],
    },
    // Terms Page
    terms: {
      title: "Termini di servizio",
      lastUpdated: "Ultimo aggiornamento: Gennaio 2026",
      sections: [
        {
          title: "1. Accettazione dei termini",
          content: "Accedendo e utilizzando il sito web di Claritech Pool Systems, accetti e ti impegni a rispettare questi Termini di servizio. Se non sei d'accordo con questi termini, ti preghiamo di non utilizzare il nostro sito web.",
        },
        {
          title: "2. Servizi",
          content: "Claritech Pool Systems fornisce servizi di sourcing per forniture piscine, supporto tecnico e servizi correlati per operatori di piscine hospitality e commerciali. Tutti i servizi sono soggetti a disponibilità e possono variare in base al mercato e allo stock del fornitore.",
        },
        {
          title: "3. Richieste commerciali",
          content: "Questo sito web è destinato esclusivamente alle comunicazioni business-to-business. Inviando una richiesta, dichiari di agire per conto di un'entità commerciale legittima.",
        },
        {
          title: "4. Proprietà intellettuale",
          content: "Tutti i contenuti di questo sito web, inclusi testi, grafica, loghi e immagini, sono di proprietà di Claritech Pool Systems e sono protetti dalle leggi applicabili sulla proprietà intellettuale.",
        },
        {
          title: "5. Limitazione di responsabilità",
          content: "Claritech Pool Systems non sarà responsabile per eventuali danni indiretti, incidentali, speciali, consequenziali o punitivi derivanti dall'uso o dall'impossibilità di utilizzare i nostri servizi o il sito web.",
        },
        {
          title: "6. Legge applicabile",
          content: "Questi Termini saranno regolati e interpretati in conformità con le leggi applicabili, senza riguardo ai principi di conflitto di leggi.",
        },
        {
          title: "7. Contatti",
          content: "Per domande su questi Termini, contattaci a",
        },
      ],
    },
  },
};

export default translations;
